import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../../components/core';

const MAX_NOTE_LENGTH = 1000;

interface AddNoteFormProps {
  onSave: (_text: string) => Promise<void>;
}

export function AddNoteForm({ onSave }: AddNoteFormProps) {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_NOTE_LENGTH) {
      setNoteError(t('customers.noteTooLong'));
      return;
    }
    setSaving(true);
    setNoteError(null);
    try {
      await onSave(trimmed);
      setNoteText('');
    } catch (err) {
      console.error('[AddNoteForm] save note error:', err);
      setNoteError(t('customers.logError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 overflow-hidden rounded border-thick border-green-200 bg-white px-[18px] py-4">
      <p className="mb-2 font-body text-xs font-bold text-gray-700">＋ {t('customers.addNote')}</p>
      <div className="rounded-sm border-thick border-dashed border-green-400 bg-white p-4">
        <textarea
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          placeholder={t('customers.notePlaceholder')}
          aria-label={t('customers.noteLabel')}
          value={noteText}
          onChange={e => {
            setNoteText(e.target.value);
            if (noteError) setNoteError(null);
          }}
          disabled={saving}
          className="w-full resize-none bg-transparent font-body text-sm text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
        />
        {noteError && (
          <p className="mt-1 font-body text-xs text-red-600">{noteError}</p>
        )}
        <div className="mt-2.5 flex items-center gap-2 border-t border-green-100 pt-2.5">
          <span className="me-auto font-body text-xs text-gray-400">
            {noteText.length}/{MAX_NOTE_LENGTH}
          </span>
          <Button size="sm" variant="primary" onClick={handleSave} disabled={saving || !noteText.trim()}>
            {saving ? <Spinner size="sm" /> : null}
            💾 {t('customers.saveNote')}
          </Button>
        </div>
      </div>
    </div>
  );
}
